/**
 * Проверяет пересечение двух прямоугольников
 *
 * @param rect1 - Первый прямоугольник
 * @param rect2 - Второй прямоугольник
 * @returns true если прямоугольники пересекаются
 */
export function isRectIntersecting(rect1, rect2) {
    return (rect1.x < rect2.x + rect2.w &&
        rect1.x + rect1.w > rect2.x &&
        rect1.y < rect2.y + rect2.h &&
        rect1.y + rect1.h > rect2.y);
}
/**
 * Получает прямоугольник элемента в мировых координатах
 *
 * @param element - Элемент доски
 * @returns Прямоугольник элемента
 */
export function getElementRect(element) {
    var _a, _b, _c, _d;
    return {
        x: ((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0,
        y: ((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0,
        w: ((_c = element.props) === null || _c === void 0 ? void 0 : _c.w) || 64,
        h: ((_d = element.props) === null || _d === void 0 ? void 0 : _d.h) || 64,
    };
}
/**
 * Получает прямоугольник узла Konva в мировых координатах
 *
 * @param node - Узел Konva
 * @returns Прямоугольник узла
 */
export function getNodeRect(node) {
    const box = node.getClientRect({ skipStroke: true });
    const stage = node.getStage();
    if (!stage) {
        return { x: 0, y: 0, w: 0, h: 0 };
    }
    const scale = stage.scaleX();
    const stageX = stage.x();
    const stageY = stage.y();
    return {
        x: (box.x - stageX) / scale,
        y: (box.y - stageY) / scale,
        w: box.width / scale,
        h: box.height / scale,
    };
}
/**
 * Находит все элементы, которые пересекаются с указанным элементом
 * Использует ту же логику, что и getIntersectingElements в useStageMouseEvents
 *
 * @param movingElement - Перемещаемый элемент
 * @param boardState - Состояние доски
 * @param excludeIds - ID элементов, которые нужно исключить из проверки
 * @returns Массив пересекающихся элементов
 */
export function findIntersectingElements(movingElement, boardState, excludeIds = []) {
    var _a, _b, _c, _d;
    const movingRect = getElementRect(movingElement);
    const intersectingElements = [];
    for (const layer of boardState.layers) {
        for (const element of layer.elements) {
            // Пропускаем исключенные элементы
            if (excludeIds.includes(element.id))
                continue;
            // Пропускаем заблокированные элементы
            if (element.locked)
                continue;
            // Используем ту же логику пересечения, что и в useStageMouseEvents
            const ex = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
            const ey = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
            const ew = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
            const eh = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
            const intersects = ex < movingRect.x + movingRect.w &&
                ex + ew > movingRect.x &&
                ey < movingRect.y + movingRect.h &&
                ey + eh > movingRect.y;
            if (intersects) {
                intersectingElements.push(element);
            }
        }
    }
    return intersectingElements;
}
/**
 * Находит максимальный zIndex среди всех элементов на доске
 *
 * @param boardState - Состояние доски
 * @returns Максимальный zIndex
 */
export function getMaxZIndex(boardState) {
    var _a;
    let maxZIndex = 0;
    for (const layer of boardState.layers) {
        for (const element of layer.elements) {
            const zIndex = (_a = element.zIndex) !== null && _a !== void 0 ? _a : 0;
            maxZIndex = Math.max(maxZIndex, zIndex);
        }
    }
    return maxZIndex;
}
/**
 * Проверяет, нужно ли изменить zIndex для перемещаемого элемента
 *
 * @param movingElement - Перемещаемый элемент
 * @param boardState - Состояние доски
 * @param excludeIds - ID элементов, которые нужно исключить из проверки
 * @returns Объект с информацией о необходимости изменения zIndex
 */
export function shouldUpdateZIndex(movingElement, boardState, excludeIds = []) {
    var _a;
    const intersectingElements = findIntersectingElements(movingElement, boardState, excludeIds);
    if (intersectingElements.length === 0) {
        return { shouldUpdate: false, intersectingElements: [] };
    }
    // Находим максимальный zIndex среди пересекающихся элементов
    const maxIntersectingZIndex = Math.max(...intersectingElements.map((el) => { var _a; return (_a = el.zIndex) !== null && _a !== void 0 ? _a : 0; }));
    const currentZIndex = (_a = movingElement.zIndex) !== null && _a !== void 0 ? _a : 0;
    // Если текущий zIndex уже больше максимального среди пересекающихся элементов,
    // то изменение не требуется
    if (currentZIndex > maxIntersectingZIndex) {
        return { shouldUpdate: false, intersectingElements };
    }
    // Устанавливаем zIndex на единицу больше максимального среди пересекающихся элементов
    const newZIndex = maxIntersectingZIndex + 1;
    return {
        shouldUpdate: true,
        newZIndex,
        intersectingElements,
    };
}
/**
 * Функция сравнения элементов для сортировки по zIndex
 * Сравнивает элементы только по их zIndex
 * Используется для правильного порядка рендеринга элементов
 *
 * @param a - Первый элемент
 * @param b - Второй элемент
 * @returns Результат сравнения для использования в sort()
 */
export function compareElementsByZIndex(a, b) {
    var _a, _b;
    const elementZIndexA = (_a = a.zIndex) !== null && _a !== void 0 ? _a : 0;
    const elementZIndexB = (_b = b.zIndex) !== null && _b !== void 0 ? _b : 0;
    return elementZIndexA - elementZIndexB;
}
